import {singleton} from "tsyringe";
import {persistence} from "../../decorators/persistence.decorator";
import {Repository} from "../../persistence/repository";
import {AuditLog} from "../entities/audit-log.entity";
import {Entity} from "../entities/entity";
import {ArrayHelper} from "../../helpers/array.helper";
import {AuditLogDTO} from "../dtos/audit-log.dto";
import {OrderParameter} from "../../connectivity/order-parameter.interface";

@singleton()
@persistence({
    entity: AuditLog
})
export class AuditLogRepository extends Repository<AuditLog<any>> {

    create(): Promise<AuditLog<any> | null> {
        return Promise.resolve(new AuditLog());
    }

    /**
     * Default sort: newest first. Kept as the fallback so existing `.entities`-only
     * mounts behave exactly as before when no `sorting` is provided.
     */
    private static readonly DEFAULT_SORTING: OrderParameter<AuditLogDTO>[] = [
        {
            key: 'createdAt',
            order: "desc"
        }
    ];

    public async findAllByEntities<TEntity extends Entity>(
        entities: TEntity[],
        sorting: OrderParameter<AuditLogDTO>[] = AuditLogRepository.DEFAULT_SORTING
    ): Promise<AuditLog<TEntity>[]> {

        const dtos = await this.entityManager.query<AuditLogDTO>(
            'audit-log',
            [{
                key: 'mainEntityUid',
                comparator: 'in',
                value: entities.map(e => e.uid)
            }, {
                key: 'property',
                comparator: 'nin',
                value: ['uid']
            }],
            0,
            1000,
            sorting
        );

        const auditLogs = await Promise.all((dtos ?? []).map(dto => this._transformDtoToEntity(dto)));

        return auditLogs.filter(ArrayHelper.notEmpty);
    }

    /**
     * Existence probe (S8 / plan 7.8): does this tenant have ANY audit-log row at all?
     * Used only when the tasklist's activity filter returns an empty result, to tell
     * "nothing matches" apart from "this tenant has no audit trail at all" (measured
     * zero rows for `com`/`ese-se`, 3.4) — the latter would otherwise read as a defect.
     *
     * Deliberately WITHOUT a `mainEntityUid` filter (unlike findAllByEntities above) —
     * this checks the tenant's audit trail in general, not one entity's. Deliberately
     * NOT `uuidsOnly` either, even though it would be the semantically exact fit for
     * "does one exist": the frontend's transport layer cannot send that parameter at
     * all (entityManager.query() builds no such field), so it would silently arrive
     * nowhere — better an honestly full row than a key nobody reads. Fetches exactly
     * one row; which one is irrelevant to "does at least one exist".
     */
    public async hasAnyAuditLog(): Promise<boolean> {
        const dtos = await this.entityManager.query<AuditLogDTO>('audit-log', [], 0, 1, []);

        return !!dtos && dtos.length > 0;
    }
}